import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { usePopover } from 'minimal-shared/hooks';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';

import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';

import { CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export type WorkspacesPopoverProps = ButtonBaseProps & {
  data?: any[];
};

export function WorkspacesPopover({ data = [], sx, ...other }: WorkspacesPopoverProps) {
  const [getMediaUrl] = useGetMediaUrlMutation();

  const mediaQuery = 'sm';

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const [workspace, setWorkspace] = useState(data[0]);
  const [tanantProfile, setTanantProfile] = useState('');

  const handleChangeWorkspace = useCallback(
    (newValue: (typeof data)[0]) => {
      setWorkspace(newValue);
      onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!data[0]?.tenant?.ProfileURL || data[0]?.tenant?.ProfileURL === '') return;

    const fetchMediaUrl = async () => {
      try {
        const { data: uploadFileUrl } = await getMediaUrl({
          fileName: data[0]?.tenant?.ProfileURL  || '',
        });
        setTanantProfile(uploadFileUrl || '');
      } catch (error) {
        console.error('Error fetching media URL:', error);
      }
    };
    if (data?.length > 0 && data[0]) {
      fetchMediaUrl();
    }
  }, [data[0]?.tenant?.ProfileURL]);

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const renderButton = () => (
    <ButtonBase
      disableRipple
      // onClick={onOpen}
      sx={[
        {
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Avatar
        alt={String(workspace?.tenant?.name || '')}
        src={tanantProfile || ''}
        sx={{ width: 30, height: 30, borderRadius: '50%' }}
      />

      <Box
        component="span"
        sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
      >
        {workspace?.tenant?.name || ''}
      </Box>

      {/* <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} /> */}
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -1.55 } },
      }}
    >
      <MenuList sx={{ width: 240 }}>
        {data &&
          data?.map((option, index) => (
            <MenuItem
              key={index}
              selected={option?.id === workspace?.id}
              onClick={() => handleChangeWorkspace(option)}
              sx={{ height: 48 }}
            >
              <Avatar
                alt={option?.tenant?.name}
                src={tanantProfile}
                sx={{ width: 30, height: 30, borderRadius: '50%' }}
              />

              <Box component="span" sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}>
                {option?.tenant?.name || ''}
              </Box>

              {/* <Label color={option.plan === 'Free' ? 'default' : 'info'}>{option.plan}</Label> */}
            </MenuItem>
          ))}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
